import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { ShoppingCart, UserCircle, List, X, Sparkle, WhatsappLogo } from "@phosphor-icons/react";
import { useState } from "react";
import { LocationSelector, HeaderSearch } from "./store/LocationSearch";
import { ChatWidget } from "./store/ChatWidget";

const WHATSAPP_URL = "https://wa.me/919152943297?text=" + encodeURIComponent("Hi Fixigo! I'd like to know more about your home services.");

export function StoreLayout({ children }) {
  const { user } = useAuth();
  const { items } = useCart();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);

  const NAV = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/sell", label: "Sell Devices" },
    { to: "/become-partner", label: "Become a Partner" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC] text-[#1E293B] font-[Manrope] selection:bg-[#4F46E5]/20">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-5 h-[68px] flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="store-logo">
            <div className="w-9 h-9 bg-[#4F46E5] rounded-xl flex items-center justify-center shadow-sm shadow-[#4F46E5]/30">
              <Sparkle size={20} weight="fill" className="text-white" />
            </div>
            <span className="font-[Outfit] font-extrabold text-xl tracking-tight">Fixigo<span className="text-[#4F46E5]"> Home</span></span>
          </Link>

          {/* Urban-Company style location + search (desktop) */}
          <LocationSelector className="hidden md:block w-[210px] shrink-0" />
          <HeaderSearch className="hidden md:block flex-1 max-w-md" />

          <nav className="hidden lg:flex items-center gap-7 shrink-0">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`storenav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className="text-sm font-semibold text-[#475569] hover:text-[#4F46E5] transition-colors duration-200 whitespace-nowrap">
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2.5 shrink-0 ml-auto md:ml-0">
            <button onClick={() => navigate("/cart")} data-testid="cart-button"
              className="relative p-2.5 rounded-xl hover:bg-[#4F46E5]/10 transition-colors duration-200">
              <ShoppingCart size={22} weight="bold" />
              {items.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#4F46E5] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-count">{items.length}</span>
              )}
            </button>
            {user && user.role === "customer" ? (
              <Link to="/account" data-testid="store-account" className="flex items-center gap-1.5 text-sm font-semibold px-3.5 py-2.5 rounded-xl border border-[#E2E8F0] hover:border-[#4F46E5] transition-colors duration-200">
                <UserCircle size={18} weight="bold" /> <span className="hidden sm:inline">Account</span>
              </Link>
            ) : (
              <Link to="/login" data-testid="store-login" className="text-sm font-bold px-5 py-2.5 rounded-xl bg-[#1E293B] text-white hover:bg-[#0f172a] transition-colors duration-200">
                Login
              </Link>
            )}
            <button className="lg:hidden p-2" onClick={() => setMenu(!menu)} data-testid="mobile-menu-toggle">
              {menu ? <X size={22} /> : <List size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile: location + search second row */}
        <div className="md:hidden border-t border-[#E2E8F0] px-5 py-2.5 flex gap-2 bg-white">
          <LocationSelector className="w-[150px] shrink-0" />
          <HeaderSearch className="flex-1" />
        </div>

        {menu && (
          <div className="lg:hidden border-t border-[#E2E8F0] px-5 py-3 space-y-1 bg-white">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setMenu(false)} className="block py-2.5 text-sm font-semibold">{n.label}</Link>
            ))}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-[#1E293B] text-white mt-20">
        <div className="max-w-7xl mx-auto px-5 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-9 h-9 bg-[#4F46E5] rounded-xl flex items-center justify-center"><Sparkle size={18} weight="fill" className="text-white" /></div>
              <span className="font-[Outfit] font-extrabold text-lg">Fixigo Home</span>
            </div>
            <p className="text-sm text-white/60 leading-relaxed">Trusted by thousands of homes — quality service, honest prices, every single time.</p>
          </div>
          <div>
            <p className="uppercase text-xs tracking-[0.2em] font-semibold text-[#4F46E5] mb-3">Company</p>
            <ul className="space-y-2.5 text-sm text-white/70">
              <li><Link to="/about" className="hover:text-white">About Us</Link></li>
              <li><Link to="/services" className="hover:text-white">Services</Link></li>
              <li><Link to="/become-partner" className="hover:text-white">Become a Partner</Link></li>
              <li><Link to="/login" className="hover:text-white">Login</Link></li>
            </ul>
          </div>
          <div>
            <p className="uppercase text-xs tracking-[0.2em] font-semibold text-[#4F46E5] mb-3">Support</p>
            <ul className="space-y-2.5 text-sm text-white/70">
              <li><a href="mailto:support@fixigohomeservices.in" className="hover:text-white">support@fixigohomeservices.in</a></li>
              <li><a href="tel:+919152943297" className="hover:text-white">+91 91529 43297</a></li>
              <li><a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-white" data-testid="footer-whatsapp"><WhatsappLogo size={16} weight="fill" className="text-[#25D366]" /> WhatsApp us</a></li>
            </ul>
          </div>
          <div>
            <p className="uppercase text-xs tracking-[0.2em] font-semibold text-[#4F46E5] mb-3">Legal</p>
            <ul className="space-y-2.5 text-sm text-white/70"><li><Link to="/terms" className="hover:text-white">Terms &amp; Conditions</Link></li><li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li></ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-white/50">
          © 2026 Fixigo Home Services. Made in India 🇮🇳
        </div>
      </footer>
      <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" data-testid="whatsapp-float"
        aria-label="Chat with us on WhatsApp"
        className="fixed left-5 bottom-5 z-50 flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold text-sm pl-3 pr-4 py-3 rounded-full shadow-xl shadow-[#25D366]/30 transition-colors duration-200">
        <WhatsappLogo size={24} weight="fill" />
        <span className="hidden sm:inline">Chat with us</span>
      </a>
      <ChatWidget />
    </div>
  );
}
